import { type ISurveyItem, type ISurveySection } from '../../../../models/SurveyTemplate'
import { type SectionItem, type ISection, type SectionBranch, type SectionResponseItem, type GetItemsWithBranchesFn } from './types'

// Separator used to combine multiple choice options into a single string
export const MULTIPLE_CHOICE_SEPERATOR = '___'

// Separator used to concatenate instructorId and surveySectionId into a modded section ID
export const SECTION_ID_SEPERATOR = '|'

// Separator between the itemId and the modded section id in a survey answer field name.
export const ANSWER_FIELD_NAME_SEPERATOR = '::'

// react-hook-form treats "|" (along with quotes, ".", "[", "]") as a special path
// character: its setter strips "|" out of a field name while its getter keeps it, so a "|"
// in a field name breaks the value round-trip (typed values never display). getModdedSectionId
// joins with "|", so that separator must be swapped for an RHF-safe one inside a field name.
// "~" is safe because the field-name parts (itemId, instructorId, surveySectionId) are all
// UUIDs (hex + hyphens) — they never contain "~", so the substitution can't cause a collision.
const ANSWER_FIELD_SECTION_SEPERATOR = '~'

/**
 * Combines instructorId and surveySectionId into a single modded section ID string.
 * If instructorId is undefined, it defaults to an empty string.
 *
 * Example Output: "123|456" or "|456"
 */
export const getModdedSectionId = (instructorId: ISurveySection['instructorId'] | undefined, surveySectionId: ISection['surveySectionId']): string => (instructorId ?? '') + SECTION_ID_SEPERATOR + surveySectionId

/**
 * CR-213: builds the react-hook-form field name (and DOM element id) for a survey answer.
 * The name must be unique per question OCCURRENCE, not per question content. Two sections
 * can legitimately reuse a single itemId — the backend intentionally dedupes questions with
 * identical prompt + type onto one survey item — so keying an answer only by
 * itemId(+instructorId) mirrored the same answer into every occurrence of that item. Folding
 * the modded section id (instructorId|surveySectionId) into the name keeps each occurrence,
 * including repeat-per-instructor copies, distinct.
 *
 * `moddedSectionId` is the value produced by `getModdedSectionId`. Transformed `ISection`
 * objects already carry it on `surveySectionId`; raw `ISurveySection` objects must build it
 * from their instructorId + surveySectionId. The modded id's "|" separator is swapped for an
 * RHF-safe character so react-hook-form's setter/getter agree on the field key (see
 * ANSWER_FIELD_SECTION_SEPERATOR). The name is only ever used as an opaque form-field key and
 * DOM id — it is never parsed back into a section id — so the substitution is safe.
 */
export const buildAnswerFieldName = (itemId: SectionItem['itemId'], moddedSectionId: ISection['surveySectionId']): string => `${itemId}${ANSWER_FIELD_NAME_SEPERATOR}${moddedSectionId.split(SECTION_ID_SEPERATOR).join(ANSWER_FIELD_SECTION_SEPERATOR)}`

/**
 * Parses a modded section ID string back into its component parts: instructorId and surveySectionId.
 * If the instructorId portion is the string 'undefined', it is returned as actual `undefined`.
 *
 * Example Input: "123|456"
 * Output: { instructorId: "123", surveySectionId: "456" }
 */
const parseModdedSectionId = (moddedSectionId: string): { instructorId: ISurveySection['instructorId'] | undefined, surveySectionId: ISection['surveySectionId'] } => {
  const [instructorID, surveySectionId] = moddedSectionId.split(SECTION_ID_SEPERATOR)

  return { instructorId: instructorID === 'undefined' ? undefined : instructorID, surveySectionId }
}

/**
 * Takes an array of section items where the `surveySectionId` is a modded string,
 * and replaces the modded ID with just the `surveySectionId` part by parsing it.
 *
 * Useful for reverting transformed data back to its original form.
 */
export const bulkParseModdedSectionIdForSectionItems = (sectionItems: SectionResponseItem[]): SectionResponseItem[] => {
  return sectionItems.map(
    item => ({ ...item, surveySectionId: parseModdedSectionId(item.surveySectionId).surveySectionId })
  )
}

/**
 * Transforms an array of ISurveySection objects into a structured hierarchy of ISection objects.
 * - Creates unique rendered section IDs by combining instructorId and surveySectionId.
 * - Preserves the raw survey section IDs for semantic branch matching.
 * - Resolves parent-child relationships by preferring an exact instructor-context match,
 *   then falling back to the non-repeated parent instance when the parent does not repeat.
 * - Handles branching logic by transforming survey items accordingly.
 *
 * Returns a tree-like structure of ISection objects.
 */
export const transformSections = (sections: ISurveySection[]): ISection[] => {
  const sectionMap: Record<string, ISection> = {}
  const transformedSections: ISection[] = []

  const findResolvedParent = (section: ISurveySection): ISurveySection | undefined => {
    if (!section.parentSurveySectionId) {
      return undefined
    }

    const exactParentMatch = sections.find((candidate) =>
      candidate.surveySectionId === section.parentSurveySectionId &&
      candidate.instructorId === section.instructorId
    )

    if (exactParentMatch) {
      return exactParentMatch
    }

    return sections.find((candidate) =>
      candidate.surveySectionId === section.parentSurveySectionId &&
      !candidate.instructorId
    )
  }

  // First pass: map each section to a new ISection structure with rendered IDs.
  sections.forEach(section => {
    const {
      surveySectionId,
      revision,
      parentSurveySectionId,
      instructorId,
      title,
      descr,
      instructorName,
      isSurveyProjectAddedSection,
      branchingFilters
    } = section

    const moddedSectionId = getModdedSectionId(instructorId, surveySectionId)

    // If branching filters exist, capture the origin information
    const origin = branchingFilters ? { itemId: branchingFilters.itemId, operation: branchingFilters.operation, type: branchingFilters.itemType } : undefined

    // Branch references should use the raw child surveySectionId so repeated child instances
    // can still attach to the same branch condition under either repeated or non-repeated parents.
    const surveyItems = getItemsWithBranches(section.surveyItems)
    const resolvedParent = findResolvedParent(section)
    const moddedparentSectionId = resolvedParent
      ? getModdedSectionId(resolvedParent.instructorId, resolvedParent.surveySectionId)
      : undefined

    // Populate sectionMap with the transformed section
    sectionMap[moddedSectionId] = {
      surveySectionId: moddedSectionId,
      rawSurveySectionId: surveySectionId,
      surveySectionRevision: revision,
      parentSurveySectionId: moddedparentSectionId,
      rawParentSurveySectionId: parentSurveySectionId,
      instructorId,
      title,
      descr,
      instructorName,
      name: section.name,
      isSelectInstructorEnabled: section?.isSelectInstructorEnabled,
      isSurveyProjectAddedSection,
      surveyItems,
      origin,
      children: []
    }
  })

  // Second pass: assign child sections to their respective parents
  sections.forEach(section => {
    const moddedSectionId = getModdedSectionId(section.instructorId, section.surveySectionId)
    const mappedSection = sectionMap[moddedSectionId]

    if (!mappedSection.parentSurveySectionId) {
      // Top-level section (no parent)
      transformedSections.push(mappedSection)
    } else {
      // Attach as a child to its parent
      const mappedParentSection = sectionMap[mappedSection.parentSurveySectionId]

      if (mappedParentSection) {
        mappedParentSection.children.push(mappedSection)
      } else {
        transformedSections.push(mappedSection)
      }
    }
  })

  return transformedSections
}

/**
 * Validates the structure of each section and its child sections.
 * Returns a record where each key is a section's `surveySectionId`,
 * and the value is `true` if that section (and all its children) pass validation.
 */
export const validateDataFormatForSections = (sections: ISection[]): Record<string, boolean> => {
  const sectionValidity: Record<ISection['surveySectionId'], boolean> = {}
  sections.forEach(section => { sectionValidity[section.surveySectionId] = validateSectionChildren(section) })

  return sectionValidity
}

/**
 * Recursively validates that each child section is correctly linked to its parent.
 * A child section is valid if:
 * - It has a corresponding branch in one of the parent section’s survey items.
 * - All of its own children are also valid.
 *
 * Returns `true` if all children (recursively) are valid, otherwise `false`.
 */
const validateSectionChildren = (section: ISection): boolean => {
  for (const child of section.children) {
    const childSectionId = child.rawSurveySectionId

    // Find the item that originated the branch to this child
    const originItemId = child.origin?.itemId
    const item = section.surveyItems.find(item => item.itemId === originItemId)

    // Check if the branch to this child section exists in the item’s branches
    const branch = item?.branches.find(branch => branch.sectionId === childSectionId)

    // If the current section has a parent, its child must have a valid branch
    if (section.parentSurveySectionId && !branch) return false

    // Recursively validate this child’s own children
    if (!validateSectionChildren(child)) return false
  }

  return true
}

/**
 * Transforms raw survey items into enriched section items, adding branching metadata.
 * - Each item is mapped to a new object with a default `value` and a `branches` array.
 * - If the item contains branching filters, they are converted to `SectionBranch` objects
 *   with a mapped (modded) section ID.
 *
 * Used to prep survey items for branching logic.
 */
export const getItemsWithBranches: GetItemsWithBranchesFn = (items) => {
  return items?.map((item) => {
    // CE-193: seed the response value from a previously submitted answer (present on
    // GET when reopening a submitted survey). Multiselect answers arrive as arrays and
    // are joined back into the internal separator-delimited string representation.
    const { answer } = item
    let value = '' // Default response value for the item
    if (Array.isArray(answer)) {
      value = answer.join(MULTIPLE_CHOICE_SEPERATOR)
    } else if (typeof answer === 'string') {
      value = answer
    }

    const { itemId, itemType, prompt, object, isRequired, revision } = item

    // Branch membership should be based on the raw child surveySectionId, not the rendered instance ID.
    const branches = item?.branchingFilters?.length > 0
      ? item?.branchingFilters?.map((branch: any) => {
        const branchingValue = branch.key
        const sectionId = branch.surveySectionId
        return { branchingValue, sectionId }
      }) as SectionBranch[]
      : []

    return { itemId, itemType, prompt, object, isRequired, value, revision, branches }
  })
}

/**
 * Searches for a survey item with a specific `itemId` within a section and its descendants.
 * - Returns the first matching item found.
 * - If not found in the current section, recursively searches through all children.
 *
 * Useful for finding the origin of a branching condition in nested sections.
 */
export const findOrigin = (section: ISection, itemId: SectionItem['itemId']): SectionItem | undefined => {
  // Check if item exists in current section
  const item = section.surveyItems.find(item => item.itemId === itemId)

  if (item) return item

  // If no children, stop the search
  if (section.children.length === 0) return undefined

  // Recursively search in children
  for (const childSection of section.children) {
    const item = findOrigin(childSection, itemId)
    if (item) return item
  }

  return undefined
}

/**
 * Resolves the origin item for a section using its direct parent instance context first.
 * This avoids cross-instance collisions when repeated sections share the same itemId.
 */
export const findOriginInBranchContext = (root: ISection, section: ISection): SectionItem | undefined => {
  if (!section.origin?.itemId || !section.parentSurveySectionId) {
    return undefined
  }

  const findSectionBySurveySectionId = (current: ISection, surveySectionId: ISection['surveySectionId']): ISection | undefined => {
    if (current.surveySectionId === surveySectionId) return current

    for (const child of current.children) {
      const matched = findSectionBySurveySectionId(child, surveySectionId)
      if (matched) return matched
    }

    return undefined
  }

  const parentSection = findSectionBySurveySectionId(root, section.parentSurveySectionId)
  if (!parentSection) {
    return undefined
  }

  const parentScopedOrigin = parentSection.surveyItems.find(item =>
    item.itemId === section.origin?.itemId &&
    item.branches.some(branch => branch.sectionId === section.rawSurveySectionId)
  )

  if (parentScopedOrigin) {
    return parentScopedOrigin
  }

  return parentSection.surveyItems.find(item => item.itemId === section.origin?.itemId)
}
